// src/components/admin/sections/AdminAboutUs.tsx - VERSIÓN PROFESIONAL COMPLETA
import React, { useState, useEffect, useRef } from 'react';
import apiElCristo, { AboutUsData, BulletPoint } from '../../../service/api_elcristo';
import './AdminAboutUs.css';

const AdminAboutUs: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bulletToDelete, setBulletToDelete] = useState<{ bullet: BulletPoint, index: number } | null>(null);
  const titleEditorRef = useRef<HTMLDivElement>(null);
  const descriptionEditorRef = useRef<HTMLDivElement>(null);

  const [aboutData, setAboutData] = useState<AboutUsData>({
    subtitle: '',
    title: '',
    description: '',
    bulletPoints: [],
    exploreMoreLink: '',
    contactNumber: '',
    images: {
      bus: '',
      girl: '',
      dot: '',
      main: '',
      secondary: '',
      borderShape: ''
    }
  });

  const [bulletInput, setBulletInput] = useState({ text: '', url: '' });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (titleEditorRef.current && aboutData.title) {
      titleEditorRef.current.innerHTML = aboutData.title;
    }
    if (descriptionEditorRef.current && aboutData.description) {
      descriptionEditorRef.current.innerHTML = aboutData.description;
    }
  }, [aboutData.title, aboutData.description]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getAboutUsData();
      console.log('📥 About Us data cargada:', data);
      setAboutData(data);
    } catch (error) {
      console.error('Error loading about us:', error);
      setMessage({ type: 'error', text: 'Error al cargar la sección' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, section?: string, field?: string) => {
    const { value } = e.target;
    
    if (section && field) {
      setAboutData(prev => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof AboutUsData] as any),
          [field]: value
        }
      }));
    } else {
      const name = e.target.name || section;
      setAboutData(prev => ({
        ...prev,
        [name!]: value
      }));
    }
  };

  const handleTitleInput = () => {
    if (titleEditorRef.current) {
      setAboutData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
    }
  };

  const handleDescriptionInput = () => {
    if (descriptionEditorRef.current) {
      setAboutData(prev => ({ ...prev, description: descriptionEditorRef.current!.innerHTML }));
    }
  };

  const applyFormat = (editorRef: React.RefObject<HTMLDivElement>, tag: string) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (selectedText) {
      const span = document.createElement('span');
      span.className = tag;
      span.textContent = selectedText;
      range.deleteContents();
      range.insertNode(span);
      
      if (editorRef === titleEditorRef) {
        setAboutData(prev => ({ ...prev, title: titleEditorRef.current!.innerHTML }));
      } else {
        setAboutData(prev => ({ ...prev, description: descriptionEditorRef.current!.innerHTML }));
      }
    }
  };

  const clearFormat = (editorRef: React.RefObject<HTMLDivElement>) => {
    if (!editorRef.current) return;
    
    const plainText = editorRef.current.innerText;
    editorRef.current.innerHTML = plainText;
    
    if (editorRef === titleEditorRef) {
      setAboutData(prev => ({ ...prev, title: plainText }));
    } else {
      setAboutData(prev => ({ ...prev, description: plainText }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, imageKey: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: '❌ Solo se permiten PNG, JPG o WEBP' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ La imagen debe ser menor a 5MB' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setUploadingImage(imageKey);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'aboutus');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Error al subir imagen');
      }
      
      const data = await response.json();
      
      console.log('✅ Imagen subida:', data.url);
      
      setAboutData(prev => ({
        ...prev,
        images: {
          ...prev.images,
          [imageKey]: data.url
        }
      }));
      
    } catch (err: any) {
      console.error('❌ Error upload:', err);
      setMessage({ type: 'error', text: `❌ ${err.message || 'Error al subir imagen'}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleAddBullet = () => {
    if (bulletInput.text.trim()) {
      setAboutData(prev => ({
        ...prev,
        bulletPoints: [...prev.bulletPoints, { 
          text: bulletInput.text.trim(), 
          url: bulletInput.url.trim() || '#' 
        }]
      }));
      setBulletInput({ text: '', url: '' });
    }
  };

  const handleOpenDeleteModal = (bullet: BulletPoint, index: number) => {
    setBulletToDelete({ bullet, index });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setBulletToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!bulletToDelete) return;

    setAboutData(prev => ({
      ...prev,
      bulletPoints: prev.bulletPoints.filter((_, i) => i !== bulletToDelete.index)
    }));

    handleCloseDeleteModal();
  };

  const handleMoveBullet = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= aboutData.bulletPoints.length) return;

    const bullets = [...aboutData.bulletPoints];
    [bullets[index], bullets[newIndex]] = [bullets[newIndex], bullets[index]];
    setAboutData(prev => ({ ...prev, bulletPoints: bullets }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      console.log('📤 Enviando about us data:', aboutData);
      
      await apiElCristo.updateSiteContent({ aboutUs: aboutData });
      
      setMessage({ type: 'success', text: '✅ Sección actualizada correctamente' });
      setTimeout(() => setMessage(null), 3000);
      
      await loadData();
    } catch (error: any) {
      console.error('❌ Error:', error);
      setMessage({ type: 'error', text: `❌ ${error.message || 'Error al guardar'}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-aboutus-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando Sección Nosotros...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-aboutus-container">
      <div className="page-header">
        <div>
          <h1><i className="fas fa-info-circle"></i> Sección Nosotros</h1>
          <p>Información sobre el colegio y sus valores</p>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check' : 'exclamation'}-circle`}></i>
          {message.text}
        </div>
      )}

      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <i className="fas fa-align-left"></i>
          Contenido
        </button>
        <button
          className={`tab ${activeTab === 'images' ? 'active' : ''}`}
          onClick={() => setActiveTab('images')}
        >
          <i className="fas fa-images"></i>
          Imágenes
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {activeTab === 'content' && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-heading"></i> Textos Principales</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label><i className="fas fa-tag"></i> Subtítulo</label>
                  <input
                    type="text"
                    name="subtitle"
                    value={aboutData.subtitle}
                    onChange={handleChange}
                    placeholder="Ej: Sobre Nosotros"
                    className="form-control"
                  />
                  <small className="hint">
                    <i className="fas fa-info-circle"></i> Texto pequeño que aparece sobre el título
                  </small>
                </div>

                <div className="form-group">
                  <label><i className="fas fa-heading"></i> Título</label>
                  
                  <div className="editor-toolbar">
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => applyFormat(titleEditorRef, 'elcristo-primary')}
                      title="Texto en rojo"
                    >
                      <i className="fas fa-font" style={{ color: '#ff0f00' }}></i>
                      Rojo
                    </button>
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => applyFormat(titleEditorRef, 'elcristo-blue')}
                      title="Texto en azul"
                    >
                      <i className="fas fa-font" style={{ color: '#70A6B1' }}></i>
                      Azul
                    </button>
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => clearFormat(titleEditorRef)}
                      title="Limpiar formato"
                    >
                      <i className="fas fa-eraser"></i>
                      Limpiar
                    </button>
                  </div>

                  <div
                    ref={titleEditorRef}
                    className="rich-editor"
                    contentEditable
                    onInput={handleTitleInput}
                    suppressContentEditableWarning
                    data-placeholder="Ej: Educación de Calidad"
                  />

                  <small className="hint">
                    <i className="fas fa-magic"></i> Selecciona texto y usa los botones de arriba para aplicar colores
                  </small>
                </div>

                <div className="form-group">
                  <label><i className="fas fa-align-left"></i> Descripción</label>
                  
                  <div className="editor-toolbar">
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => applyFormat(descriptionEditorRef, 'elcristo-primary')}
                      title="Texto en rojo"
                    >
                      <i className="fas fa-font" style={{ color: '#ff0f00' }}></i>
                      Rojo
                    </button>
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => applyFormat(descriptionEditorRef, 'elcristo-blue')}
                      title="Texto en azul"
                    >
                      <i className="fas fa-font" style={{ color: '#70A6B1' }}></i>
                      Azul
                    </button>
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => clearFormat(descriptionEditorRef)}
                      title="Limpiar formato"
                    >
                      <i className="fas fa-eraser"></i>
                      Limpiar
                    </button>
                  </div>

                  <div
                    ref={descriptionEditorRef}
                    className="rich-editor rich-editor-large"
                    contentEditable
                    onInput={handleDescriptionInput}
                    suppressContentEditableWarning
                    data-placeholder="Descripción detallada sobre el colegio, sus valores y misión"
                  />

                  <small className="hint">
                    <i className="fas fa-magic"></i> Selecciona texto y aplica formato con los botones
                  </small>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-list-ul"></i> Puntos Destacados ({aboutData.bulletPoints.length})</h3>
              </div>
              <div className="card-body">
                <div className="bullet-input-group">
                  <input
                    type="text"
                    value={bulletInput.text}
                    onChange={(e) => setBulletInput(prev => ({ ...prev, text: e.target.value }))}
                    placeholder="Texto del punto destacado"
                    className="form-control"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBullet())}
                  />
                  <input
                    type="text"
                    value={bulletInput.url}
                    onChange={(e) => setBulletInput(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="URL (opcional)"
                    className="form-control"
                  />
                  <button type="button" className="btn btn-primary" onClick={handleAddBullet}>
                    <i className="fas fa-plus"></i>
                    Agregar
                  </button>
                </div>

                {aboutData.bulletPoints.length === 0 ? (
                  <div className="empty-state-mini">
                    <i className="fas fa-list-ul"></i>
                    <p>No hay puntos destacados</p>
                  </div>
                ) : (
                  <div className="bullet-list">
                    {aboutData.bulletPoints.map((bullet, index) => (
                      <div key={index} className="bullet-item">
                        <div className="bullet-item-drag">
                          <i className="fas fa-grip-vertical"></i>
                        </div>
                        <div className="bullet-item-content">
                          <i className="fas fa-check-circle"></i>
                          <span>{bullet.text}</span>
                          {bullet.url && bullet.url !== '#' && (
                            <a href={bullet.url} target="_blank" rel="noopener noreferrer" className="bullet-url">
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                          )}
                        </div>
                        <div className="bullet-item-actions">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => handleMoveBullet(index, 'up')}
                            disabled={index === 0}
                            title="Subir"
                          >
                            <i className="fas fa-arrow-up"></i>
                          </button>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => handleMoveBullet(index, 'down')}
                            disabled={index === aboutData.bulletPoints.length - 1}
                            title="Bajar"
                          >
                            <i className="fas fa-arrow-down"></i>
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleOpenDeleteModal(bullet, index)}
                            title="Eliminar"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-phone"></i> Información de Contacto</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fas fa-phone"></i> Número de Contacto</label>
                    <input
                      type="text"
                      name="contactNumber"
                      value={aboutData.contactNumber}
                      onChange={handleChange}
                      placeholder="+56 9 4696 6778"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label><i className="fas fa-link"></i> Link "Explorar Más"</label>
                    <input
                      type="text"
                      name="exploreMoreLink"
                      value={aboutData.exploreMoreLink}
                      onChange={handleChange}
                      placeholder="#nosotros o /nosotros"
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'images' && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h3><i className="fas fa-images"></i> Galería de Imágenes</h3>
              </div>
              <div className="card-body">
                <h4 className="section-subtitle"><i className="fas fa-star"></i> Imágenes Principales</h4>
                
                <div className="images-grid">
                  {/* Main Image */}
                  <div className="image-upload-card">
                    <div className="image-upload-header">
                      <i className="fas fa-image"></i>
                      <h4>Imagen Principal</h4>
                    </div>
                    
                    {aboutData.images.main && (
                      <div className="image-preview-large">
                        <img 
                          src={aboutData.images.main.startsWith('http') 
                            ? aboutData.images.main 
                            : `${window.location.origin}${aboutData.images.main}`
                          } 
                          alt="Main" 
                        />
                      </div>
                    )}
                    
                    <input
                      type="text"
                      value={aboutData.images.main}
                      onChange={(e) => handleChange(e, 'images', 'main')}
                      placeholder="/assets/kidsa/assets/img/about/02.jpg"
                      className="form-control"
                      style={{ marginBottom: '10px' }}
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload-full"
                      disabled={uploadingImage === 'main'}
                      onClick={() => document.getElementById('main-image-input')?.click()}
                    >
                      {uploadingImage === 'main' ? (
                        <><div className="spinner-small"></div> Subiendo...</>
                      ) : (
                        <><i className="fas fa-cloud-upload-alt"></i> Subir Imagen Principal</>
                      )}
                    </button>
                    <input
                      id="main-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'main')}
                      style={{ display: 'none' }}
                    />
                    
                    <small className="hint">
                      <i className="fas fa-info-circle"></i> PNG, JPG, WEBP (máx 5MB)
                    </small>
                  </div>

                  {/* Secondary Image */}
                  <div className="image-upload-card">
                    <div className="image-upload-header">
                      <i className="fas fa-image"></i>
                      <h4>Imagen Secundaria</h4>
                    </div>
                    
                    {aboutData.images.secondary && (
                      <div className="image-preview-large">
                        <img 
                          src={aboutData.images.secondary.startsWith('http') 
                            ? aboutData.images.secondary 
                            : `${window.location.origin}${aboutData.images.secondary}`
                          } 
                          alt="Secondary" 
                        />
                      </div>
                    )}
                    
                    <input
                      type="text"
                      value={aboutData.images.secondary}
                      onChange={(e) => handleChange(e, 'images', 'secondary')}
                      placeholder="/assets/kidsa/assets/img/about/02.png"
                      className="form-control"
                      style={{ marginBottom: '10px' }}
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload-full"
                      disabled={uploadingImage === 'secondary'}
                      onClick={() => document.getElementById('secondary-image-input')?.click()}
                    >
                      {uploadingImage === 'secondary' ? (
                        <><div className="spinner-small"></div> Subiendo...</>
                      ) : (
                        <><i className="fas fa-cloud-upload-alt"></i> Subir Imagen Secundaria</>
                      )}
                    </button>
                    <input
                      id="secondary-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'secondary')}
                      style={{ display: 'none' }}
                    />
                    
                    <small className="hint">
                      <i className="fas fa-info-circle"></i> PNG, JPG, WEBP (máx 5MB)
                    </small>
                  </div>
                </div>

                <hr className="divider" />

                <h4 className="section-subtitle"><i className="fas fa-shapes"></i> Elementos Decorativos</h4>

                <div className="images-grid-small">
                  {/* Bus */}
                  <div className="image-upload-card-small">
                    <div className="image-upload-header-small">
                      <i className="fas fa-bus"></i>
                      <span>Bus</span>
                    </div>
                    
                    {aboutData.images.bus && (
                      <div className="image-preview-small">
                        <img 
                          src={aboutData.images.bus.startsWith('http') 
                            ? aboutData.images.bus 
                            : `${window.location.origin}${aboutData.images.bus}`
                          } 
                          alt="Bus" 
                        />
                      </div>
                    )}
                    
                    <input
                      type="text"
                      value={aboutData.images.bus}
                      onChange={(e) => handleChange(e, 'images', 'bus')}
                      placeholder="/assets/kidsa/assets/img/about/bus.png"
                      className="form-control form-control-small"
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload-small"
                      disabled={uploadingImage === 'bus'}
                      onClick={() => document.getElementById('bus-image-input')?.click()}
                    >
                      {uploadingImage === 'bus' ? (
                        <><div className="spinner-small"></div></>
                      ) : (
                        <><i className="fas fa-upload"></i> Subir</>
                      )}
                    </button>
                    <input
                      id="bus-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'bus')}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Girl */}
                  <div className="image-upload-card-small">
                    <div className="image-upload-header-small">
                      <i className="fas fa-child"></i>
                      <span>Niña</span>
                    </div>
                    
                    {aboutData.images.girl && (
                      <div className="image-preview-small">
                        <img 
                          src={aboutData.images.girl.startsWith('http') 
                            ? aboutData.images.girl 
                            : `${window.location.origin}${aboutData.images.girl}`
                          } 
                          alt="Girl" 
                        />
                      </div>
                    )}
                    
                    <input
                      type="text"
                      value={aboutData.images.girl}
                      onChange={(e) => handleChange(e, 'images', 'girl')}
                      placeholder="/assets/kidsa/assets/img/about/backpack.png"
                      className="form-control form-control-small"
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload-small"
                      disabled={uploadingImage === 'girl'}
                      onClick={() => document.getElementById('girl-image-input')?.click()}
                    >
                      {uploadingImage === 'girl' ? (
                        <><div className="spinner-small"></div></>
                      ) : (
                        <><i className="fas fa-upload"></i> Subir</>
                      )}
                    </button>
                    <input
                      id="girl-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'girl')}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Dot */}
                  <div className="image-upload-card-small">
                    <div className="image-upload-header-small">
                      <i className="fas fa-circle"></i>
                      <span>Dot</span>
                    </div>
                    
                    {aboutData.images.dot && (
                      <div className="image-preview-small">
                        <img 
                          src={aboutData.images.dot.startsWith('http') 
                            ? aboutData.images.dot 
                            : `${window.location.origin}${aboutData.images.dot}`
                          } 
                          alt="Dot" 
                        />
                      </div>
                    )}
                    
                    <input
                      type="text"
                      value={aboutData.images.dot}
                      onChange={(e) => handleChange(e, 'images', 'dot')}
                      placeholder="/assets/kidsa/assets/img/about/dot.png"
                      className="form-control form-control-small"
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload-small"
                      disabled={uploadingImage === 'dot'}
                      onClick={() => document.getElementById('dot-image-input')?.click()}
                    >
                      {uploadingImage === 'dot' ? (
                        <><div className="spinner-small"></div></>
                      ) : (
                        <><i className="fas fa-upload"></i> Subir</>
                      )}
                    </button>
                    <input
                      id="dot-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'dot')}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Border Shape */}
                  <div className="image-upload-card-small">
                    <div className="image-upload-header-small">
                      <i className="fas fa-border-style"></i>
                      <span>Border</span>
                    </div>
                    
                    {aboutData.images.borderShape && (
                      <div className="image-preview-small">
                        <img 
                          src={aboutData.images.borderShape.startsWith('http') 
                            ? aboutData.images.borderShape 
                            : `${window.location.origin}${aboutData.images.borderShape}`
                          } 
                          alt="Border" 
                        />
                      </div>
                    )}
                    
                    <input
                      type="text"
                      value={aboutData.images.borderShape}
                      onChange={(e) => handleChange(e, 'images', 'borderShape')}
                      placeholder="/assets/kidsa/assets/img/about/border-shape-1.png"
                      className="form-control form-control-small"
                    />
                    
                    <button
                      type="button"
                      className="btn btn-upload-small"
                      disabled={uploadingImage === 'borderShape'}
                      onClick={() => document.getElementById('border-image-input')?.click()}
                    >
                      {uploadingImage === 'borderShape' ? (
                        <><div className="spinner-small"></div></>
                      ) : (
                        <><i className="fas fa-upload"></i> Subir</>
                      )}
                    </button>
                    <input
                      id="border-image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'borderShape')}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={loadData}
            disabled={saving}
          >
            <i className="fas fa-undo"></i>
            Recargar
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || uploadingImage !== null}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* PREVIEW SECTION */}
      <div className="aboutus-preview-section">
        <div className="aboutus-preview-header">
          <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        </div>
        
        <div className="aboutus-preview">
          <div className="aboutus-preview-images">
            <div className="preview-main-image">
              {aboutData.images.main && (
                <img 
                  src={aboutData.images.main.startsWith('http') 
                    ? aboutData.images.main 
                    : `${window.location.origin}${aboutData.images.main}`
                  } 
                  alt="Main" 
                />
              )}
            </div>
            <div className="preview-secondary-image">
              {aboutData.images.secondary && (
                <img 
                  src={aboutData.images.secondary.startsWith('http') 
                    ? aboutData.images.secondary 
                    : `${window.location.origin}${aboutData.images.secondary}`
                  } 
                  alt="Secondary" 
                />
              )}
            </div>
            <div className="preview-border-shape">
              {aboutData.images.borderShape && (
                <img 
                  src={aboutData.images.borderShape.startsWith('http') 
                    ? aboutData.images.borderShape 
                    : `${window.location.origin}${aboutData.images.borderShape}`
                  } 
                  alt="Border" 
                />
              )}
            </div>
          </div>

          <div className="aboutus-preview-content">
            <div className="preview-section-title">
              <span className="preview-subtitle">{aboutData.subtitle || 'Subtítulo'}</span>
              <h2 
                className="preview-title"
                dangerouslySetInnerHTML={{ 
                  __html: aboutData.title || 'Título <span class="elcristo-primary">Principal</span>' 
                }}
              />
            </div>
            
            <p 
              className="preview-description"
              dangerouslySetInnerHTML={{ 
                __html: aboutData.description || 'Descripción sobre el colegio, sus valores y misión...' 
              }}
            />

            <div className="preview-bullet-lists">
              <ul className="preview-bullet-list">
                {(aboutData.bulletPoints.length > 0 ? aboutData.bulletPoints.slice(0, 4) : 
                  [{text: 'Punto destacado 1', url: '#'}, {text: 'Punto destacado 2', url: '#'}, 
                   {text: 'Punto destacado 3', url: '#'}, {text: 'Punto destacado 4', url: '#'}]
                ).map((point, index) => (
                  <li key={index}>
                    <i className="fas fa-check-circle"></i>
                    <span>{point.text}</span>
                  </li>
                ))}
              </ul>
              <ul className="preview-bullet-list">
                {(aboutData.bulletPoints.length > 4 ? aboutData.bulletPoints.slice(4) : 
                  [{text: 'Punto destacado 5', url: '#'}, {text: 'Punto destacado 6', url: '#'}, 
                   {text: 'Punto destacado 7', url: '#'}, {text: 'Punto destacado 8', url: '#'}]
                ).map((point, index) => (
                  <li key={index}>
                    <i className="fas fa-check-circle"></i>
                    <span>{point.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="preview-contact-info">
              <div className="preview-contact-phone">
                <div className="preview-phone-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <div className="preview-phone-content">
                  <span>WhatsApp</span>
                  <h5>{aboutData.contactNumber || '+56 9 4696 6778'}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements note */}
        <div className="aboutus-preview-note">
          <div className="preview-note-item">
            <i className="fas fa-bus"></i>
            <span>Bus: {aboutData.images.bus ? aboutData.images.bus.split('/').pop() : 'No configurado'}</span>
          </div>
          <div className="preview-note-item">
            <i className="fas fa-child"></i>
            <span>Alumno: {aboutData.images.girl ? aboutData.images.girl.split('/').pop() : 'No configurado'}</span>
          </div>
          <div className="preview-note-item">
            <i className="fas fa-circle"></i>
            <span>Dot: {aboutData.images.dot ? aboutData.images.dot.split('/').pop() : 'No configurado'}</span>
          </div>
        </div>
      </div>

      {/* MODAL DE CONFIRMACIÓN ELIMINAR */}
      {showDeleteModal && bulletToDelete && (
        <div className="team-modal-overlay">
          <div className="team-modal-container" style={{ maxWidth: '500px' }}>
            <div className="team-modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle" style={{ color: '#ff0f00' }}></i>
                Confirmar Eliminación
              </h3>
              <button className="team-modal-close" onClick={handleCloseDeleteModal} type="button" disabled={saving}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="team-modal-body">
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                ¿Estás seguro que deseas eliminar el punto destacado <strong>"{bulletToDelete.bullet.text}"</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="team-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseDeleteModal} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAboutUs;